import { Fragment, initializeConnectedObserver, jsx } from '@amzn/shiv'
import { initializeLogging } from '@/core/initializeLogging'
import { initializeErrorHandling } from '@/errorHandling'
import { PartyView } from '@/party/PartyView'
import { ErrorsView } from '@/errorHandling/ErrorsView'
import { logInfo } from '@amzn/vinyl-util'
import { bullfrogVersion } from '@/version'
import { ctx } from '@/util/context'
import { initializeDeviceInfo } from '@/device/device'
import { RouterImpl, routerRef } from '@/router/router'
import { registerPartyRoutes } from '@/party/partyRoutes'
import { getSessionId, validateSession } from '@/party/partySessionService'

const { target } = ctx('app')

const REVALIDATE_INTERVAL_MS = 5 * 60 * 1000

routerRef.set(
    () =>
        new RouterImpl({
            useHashbang: true,
        })
)

logInfo(target, `👋 Bullfrog Party Mode ${bullfrogVersion.str}`)

function showBlocked(message: string) {
    document.body.innerHTML = ''
    document.body.append(
        <div className="partyBlocked">
            <h1>🎵</h1>
            <p>{message}</p>
        </div>
    )
}

function promptGuestName(): Promise<string> {
    const existing = sessionStorage.getItem('partyGuestName')
    if (existing) return Promise.resolve(existing)

    return new Promise((resolve) => {
        const input = <input type="text" placeholder="Your name" className="guestNameInput" /> as HTMLInputElement
        const submit = (
            <button className="button guestNameSubmit" onclick={() => {
                const name = input.value.trim() || 'Someone'
                sessionStorage.setItem('partyGuestName', name)
                resolve(name)
            }}>
                Join the Party 🎉
            </button>
        )
        input.onkeydown = (e) => {
            if (e.key === 'Enter') (submit as HTMLButtonElement).click()
        }
        document.body.innerHTML = ''
        document.body.append(
            <div className="partyNamePrompt">
                <h1>🎵 Welcome to the Party!</h1>
                <p>What's your name?</p>
                {input}
                {submit}
            </div>
        )
        input.focus()
    })
}

void (async () => {
    initializeErrorHandling()
    initializeLogging()
    initializeConnectedObserver()
    await initializeDeviceInfo()

    const sessionId = getSessionId()
    if (!sessionId) {
        showBlocked('No session provided. Scan the QR code to join a party!')
        return
    }

    const result = await validateSession(sessionId)
    if (!result.valid) {
        showBlocked(result.error ?? 'Invalid session')
        return
    }

    await promptGuestName()

    document.body.innerHTML = ''
    document.body.append(
        <>
            <ErrorsView />
            <PartyView />
            <div id="toastContainer" visible={false}>
                <div className="messages" />
            </div>
        </>
    )

    registerPartyRoutes()

    // Re-validate session every 5 minutes
    setInterval(async () => {
        const check = await validateSession(sessionId).catch(() => ({ valid: false, error: 'Connection lost' }))
        if (!check.valid) {
            showBlocked(check.error ?? 'Session ended')
        }
    }, REVALIDATE_INTERVAL_MS)
})()
